import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { bookingService } from '../../services/bookingService';
import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import BackButton from '../../components/BackButton';
import { formatNumber } from '../../utils/formatNumber';
import { getPhotographyTypeLabelAr } from '../../utils/bookingLabels';
import { formatTimeToArabic } from '../../utils/formatDate';

const MOYASAR_CSS_URL = 'https://cdn.moyasar.com/mpf/1.14.0/moyasar.css';
const MOYASAR_JS_URL  = 'https://cdn.moyasar.com/mpf/1.14.0/moyasar.js';
const APP_URL         = import.meta.env.VITE_APP_URL || 'https://www.mashhad.sa';

// هل التطبيق يعمل كتطبيق جوال مغلّف؟
const IS_NATIVE = Capacitor.isNativePlatform();

// هل تُفتح الصفحة داخل InAppBrowser (مرسلة من التطبيق النيتف مع رموز المصادقة في الـ hash)؟
const HAS_HASH_TOKENS =
  typeof window !== 'undefined' && window.location.hash.includes('at=');

const BookingPayment = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();

  const [booking, setBooking]             = useState(null);
  const [loading, setLoading]             = useState(true);
  const [formReady, setFormReady]         = useState(false);
  const [formError, setFormError]         = useState('');
  const [timeRemaining, setTimeRemaining] = useState(null);
  // الجلسة جاهزة: مباشرةً إذا كنا على النيتف أو على الويب بدون hash tokens
  const [sessionReady, setSessionReady]   = useState(IS_NATIVE || !HAS_HASH_TOKENS);
  const [nativeLoading, setNativeLoading] = useState(false);

  const timerRef      = useRef(null);
  const formInitRef   = useRef(false);
  const bookingRef    = useRef(null);

  useEffect(() => { bookingRef.current = booking; }, [booking]);

  // ── مرحلة 0: استعادة الجلسة من الـ hash (وضع InAppBrowser) ─────────────────
  // عندما يفتح التطبيق النيتف صفحة الدفع في InAppBrowser، يُمرّر رموز المصادقة
  // في fragment الـ URL (#at=...&rt=...) لأنه لا يُرسل للسيرفر ولا يظهر في اللوجز.
  useEffect(() => {
    if (IS_NATIVE || !HAS_HASH_TOKENS) return; // لا شيء لتهيئته
    const initSession = async () => {
      try {
        const params = new URLSearchParams(window.location.hash.substring(1));
        const at = params.get('at');
        const rt = params.get('rt');
        if (at && rt) {
          await supabase.auth.setSession({
            access_token:  decodeURIComponent(at),
            refresh_token: decodeURIComponent(rt),
          });
          // نظّف الـ hash من URL حتى لا يُرى في شريط العنوان
          window.history.replaceState(
            {},
            '',
            window.location.pathname + window.location.search,
          );
        }
      } catch (e) {
        console.warn('[BookingPayment] session init from hash failed:', e);
      } finally {
        setSessionReady(true);
      }
    };
    initSession();
  }, []);

  // ── تحميل الحجز (بعد جهوزية الجلسة) ───────────────────────────────────────
  useEffect(() => {
    if (!bookingId || !sessionReady) return;
    loadBooking();
    return () => clearInterval(timerRef.current);
  }, [bookingId, sessionReady]);

  const loadBooking = async () => {
    try {
      setLoading(true);
      const data = await bookingService.getBookingById(bookingId);
      setBooking(data);

      if (data?.status !== 'awaiting_payment') {
        if (data?.status === 'paid_confirmed')
          navigate(`/booking-confirmation?id=${bookingId}`);
        else if (data?.status === 'expired')
          navigate(`/booking-expired/${bookingId}`);
        else if (['cancelled_by_customer', 'cancelled_by_admin', 'cancelled'].includes(data?.status))
          navigate('/customer-dashboard/bookings');
        else
          navigate(`/booking-waiting/${bookingId}`);
      }
    } catch (error) {
      console.error('Failed to load booking:', error);
    } finally {
      setLoading(false);
    }
  };

  // ── تشغيل ميسر بعد تحميل الحجز (ويب + InAppBrowser فقط، لا النيتف الأصلي) ──
  useEffect(() => {
    if (!booking || booking.status !== 'awaiting_payment') return;

    startTimer(booking.paymentExpiresAt);

    // على تطبيق الجوال النيتف نعرض زر "فتح نافذة الدفع" بدلاً من تضمين النموذج
    if (IS_NATIVE) return;

    const timeout = setTimeout(() => loadMoyasarScript(), 100);
    return () => clearTimeout(timeout);
  }, [booking]);

  // ── فتح InAppBrowser للدفع (تطبيق الجوال المغلّف فقط) ──────────────────────
  const openNativePayment = async () => {
    try {
      setNativeLoading(true);
      setFormError('');

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate('/customer-login');
        return;
      }

      // نُمرّر رموز المصادقة في الـ hash fragment — لا يُرسل للسيرفر أبداً
      // نفتح pay.html (صفحة خفيفة ~155KB) بدلاً من React bundle الكامل (~2MB)
      const at = encodeURIComponent(session.access_token);
      const rt = encodeURIComponent(session.refresh_token);
      const url = `${APP_URL}/pay.html?booking_id=${bookingId}#at=${at}&rt=${rt}`;

      await Browser.open({
        url,
        toolbarColor: '#2563EB', // لون التطبيق الأساسي
      });

      // عند إغلاق النافذة: نُحدّث حالة الحجز تلقائياً
      const listener = await Browser.addListener('browserFinished', async () => {
        await loadBooking();
        listener.remove();
      });
    } catch (err) {
      console.error('[BookingPayment] openNativePayment failed:', err);
      setFormError('تعذّر فتح نافذة الدفع. حاول مرة أخرى.');
    } finally {
      setNativeLoading(false);
    }
  };

  const loadMoyasarScript = () => {
    if (formInitRef.current) return;

    if (window.Moyasar) {
      initMoyasarForm();
      return;
    }

    if (!document.getElementById('moyasar-css')) {
      const link = document.createElement('link');
      link.id = 'moyasar-css';
      link.rel = 'stylesheet';
      link.href = MOYASAR_CSS_URL;
      document.head.appendChild(link);
    }

    const existingScript = document.getElementById('moyasar-js');
    if (existingScript) {
      existingScript.addEventListener('load', initMoyasarForm);
      return;
    }

    const script = document.createElement('script');
    script.id = 'moyasar-js';
    script.src = MOYASAR_JS_URL;
    script.onload = initMoyasarForm;
    script.onerror = () => setFormError('تعذّر تحميل بوابة الدفع. تحقق من اتصالك بالإنترنت.');
    document.head.appendChild(script);
  };

  const initMoyasarForm = useCallback(async () => {
    if (formInitRef.current) return;

    let currentBooking = bookingRef.current;
    try {
      const freshData = await bookingService.getBookingById(bookingId);
      if (freshData) {
        currentBooking = freshData;
        bookingRef.current = freshData;
        setBooking(freshData);
      }
    } catch (e) {
      console.warn('Fresh booking fetch failed, using cached data:', e);
    }

    if (!currentBooking) return;

    const publishableKey = import.meta.env.VITE_MOYASAR_PUBLISHABLE_KEY;
    if (!publishableKey || publishableKey.includes('XXXX')) {
      setFormError('مفتاح ميسر غير مضبوط. أضف VITE_MOYASAR_PUBLISHABLE_KEY في ملف .env وأعد تشغيل الخادم.');
      return;
    }

    const price = currentBooking.finalPriceSar
      ?? currentBooking.finalPrice
      ?? currentBooking.final_price_sar
      ?? currentBooking.final_price
      ?? currentBooking.price
      ?? 0;
    const amountHalalas = Math.round(price * 100);

    console.log(`[BookingPayment] السعر من DB: ${price} ر.س = ${amountHalalas} هللة`);

    if (amountHalalas < 100) {
      setFormError(`المبلغ غير صحيح (${price} ر.س). الحد الأدنى للدفع عبر ميسر هو 1 ر.س.`);
      return;
    }

    const formEl = document.querySelector('.mysr-form');
    if (!formEl) {
      setTimeout(initMoyasarForm, 300);
      return;
    }

    try {
      window.Moyasar.init({
        element: '.mysr-form',
        amount: amountHalalas,
        currency: 'SAR',
        description: `حجز تصوير #${bookingId?.slice(0, 8)}`,
        publishable_api_key: publishableKey,
        callback_url: `${APP_URL}/booking-payment-callback?booking_id=${bookingId}`,
        supported_networks: ['visa', 'mastercard', 'mada'],
        methods: ['creditcard', 'applepay'],
        apple_pay: {
          country: 'SA',
          label: 'Mashhad',
          validate_merchant_url: 'https://api.moyasar.com/v1/applepay/initiate',
        },
        on_completed: async (payment) => {
          try {
            await supabase
              .from('bookings')
              .update({ payment_transaction_id: payment.id, updated_at: new Date().toISOString() })
              .eq('id', bookingId);
          } catch (e) {
            console.warn('on_completed save failed:', e);
          }
        },
      });

      formInitRef.current = true;
      setFormReady(true);
    } catch (err) {
      console.error('Moyasar.init failed:', err);
      setFormError(`خطأ في تهيئة بوابة الدفع: ${err.message}`);
    }
  }, [bookingId]);

  // ── مؤقت انتهاء الصلاحية ───────────────────────────────────────────────────
  const startTimer = (expiresAt) => {
    if (!expiresAt) return;
    clearInterval(timerRef.current);
    const tick = () => {
      const diff = new Date(expiresAt) - new Date();
      if (diff <= 0) {
        setTimeRemaining({ minutes: 0, seconds: 0, expired: true });
        clearInterval(timerRef.current);
        return;
      }
      setTimeRemaining({
        minutes: Math.floor(diff / 60000),
        seconds: Math.floor((diff % 60000) / 1000),
        expired: false,
      });
    };
    tick();
    timerRef.current = setInterval(tick, 1000);
  };

  // ── تحديث الحالة في قاعدة البيانات فور انتهاء المهلة ─────────────────────
  useEffect(() => {
    if (!timeRemaining?.expired || !bookingId) return;
    const markExpired = async () => {
      try {
        await supabase
          .from('bookings')
          .update({ status: 'expired', updated_at: new Date().toISOString() })
          .eq('id', bookingId)
          .eq('status', 'awaiting_payment'); // لا تعدّل إذا تغيّرت الحالة مسبقاً
      } catch (_) {}
      navigate(`/booking-expired/${bookingId}`, { replace: true });
    };
    markExpired();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeRemaining?.expired]);

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-slate-50 to-slate-50 flex items-center justify-center" dir="rtl">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-slate-600">جاري التحميل...</p>
        </div>
      </div>
    );
  }

  if (!booking || booking.status !== 'awaiting_payment') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-slate-50 to-slate-50 flex items-center justify-center" dir="rtl">
        <div className="text-center">
          <Icon name="AlertCircle" size={64} className="mx-auto mb-4 text-error" />
          <h2 className="text-2xl font-bold text-slate-900 mb-2">الحجز غير جاهز للدفع</h2>
          <Button onClick={() => navigate('/customer-dashboard/bookings')} iconName="ArrowRight">
            العودة إلى الحجوزات
          </Button>
        </div>
      </div>
    );
  }

  const isExpired = timeRemaining?.expired;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-slate-50 to-slate-50" dir="rtl">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 py-12">
        <div className="max-w-2xl mx-auto">
          <div className="mb-4">
            <BackButton to="/customer-dashboard/bookings" label="العودة إلى الحجوزات" />
          </div>

          <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8 md:p-10">

            {/* Header */}
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="CreditCard" size={36} className="text-primary" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-2">إتمام الدفع</h1>
              <p className="text-slate-500 text-sm">يرجى إكمال عملية الدفع لتأكيد الحجز</p>

              {timeRemaining && !isExpired && (
                <div className="mt-3 inline-flex items-center gap-2 bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-4 py-1.5 text-sm font-semibold">
                  <Icon name="Clock" size={14} />
                  متبقي {timeRemaining.minutes}:{String(timeRemaining.seconds).padStart(2, '0')}
                </div>
              )}
              {isExpired && (
                <div className="mt-3 inline-flex items-center gap-2 bg-red-50 text-red-700 border border-red-200 rounded-full px-4 py-1.5 text-sm font-semibold">
                  <Icon name="AlertCircle" size={14} />
                  انتهت مهلة الدفع
                </div>
              )}
            </div>

            {/* تفاصيل الحجز */}
            <div className="bg-slate-50 rounded-xl p-5 mb-8 text-right">
              <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Icon name="FileText" size={16} />
                تفاصيل الحجز
              </h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">المصور</span>
                  <span className="font-medium text-slate-900">
                    {booking.photographer?.full_name || booking.photographer?.fullName || 'مصور'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">نوع الخدمة</span>
                  <span className="font-medium text-slate-900">
                    {getPhotographyTypeLabelAr(booking.photographyType || booking.serviceType) || 'تصوير'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">التاريخ والوقت</span>
                  <span className="font-medium text-slate-900">
                    {booking.bookingDate
                      ? new Date(booking.bookingDate).toLocaleDateString('ar-SA-u-ca-gregory-nu-latn', {
                          day: 'numeric', month: 'long', year: 'numeric',
                        })
                      : 'غير محدد'}{' '}
                    - {formatTimeToArabic(booking.bookingTime)}
                  </span>
                </div>
                <div className="flex justify-between border-t-2 border-slate-200 pt-3 mt-2">
                  <span className="text-slate-700 font-bold">المبلغ الإجمالي</span>
                  <span className="font-bold text-primary text-xl">
                    {formatNumber(
                        booking.finalPriceSar
                        ?? booking.finalPrice
                        ?? booking.final_price_sar
                        ?? booking.final_price
                        ?? booking.price
                        ?? 0
                      )} ر.س
                  </span>
                </div>
              </div>
            </div>

            {/* محتوى الدفع */}
            {isExpired ? (
              <div className="text-center py-8">
                <Icon name="Clock" size={48} className="mx-auto mb-4 text-slate-400" />
                <p className="text-slate-600 mb-4">انتهت مهلة الدفع لهذا الحجز</p>
                <Button variant="outline" onClick={() => navigate('/customer-dashboard/bookings')} iconName="ArrowRight">
                  العودة إلى الحجوزات
                </Button>
              </div>
            ) : formError ? (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center">
                <Icon name="AlertCircle" size={32} className="mx-auto mb-2 text-red-500" />
                <p className="text-red-700 text-sm font-medium">{formError}</p>
                <button
                  className="mt-3 text-xs text-red-500 underline"
                  onClick={() => {
                    setFormError('');
                    if (IS_NATIVE) {
                      openNativePayment();
                    } else {
                      formInitRef.current = false;
                      loadMoyasarScript();
                    }
                  }}
                >
                  إعادة المحاولة
                </button>
              </div>
            ) : IS_NATIVE ? (
              /* ── وضع الجوال المغلّف: زر يفتح InAppBrowser ─────────────────── */
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-100 rounded-2xl p-6 text-center">
                  <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="Shield" size={28} className="text-primary" />
                  </div>
                  <p className="text-slate-800 font-semibold mb-1">دفع آمن ومشفّر</p>
                  <p className="text-slate-500 text-sm leading-relaxed">
                    ستُفتح نافذة دفع آمنة داخل التطبيق مباشرةً
                  </p>
                </div>

                <button
                  onClick={openNativePayment}
                  disabled={nativeLoading}
                  className="w-full flex items-center justify-center gap-3 bg-primary hover:bg-primary/90 disabled:opacity-60 text-white font-semibold text-base rounded-xl px-6 py-4 transition-all duration-200 shadow-md shadow-primary/20 active:scale-95"
                >
                  {nativeLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                      <span>جاري الفتح...</span>
                    </>
                  ) : (
                    <>
                      <Icon name="Lock" size={18} />
                      <span>متابعة إلى الدفع الآمن</span>
                    </>
                  )}
                </button>

                <div className="flex items-center justify-center gap-2 text-xs text-slate-400 pt-1">
                  <Icon name="CreditCard" size={12} />
                  <span>Visa · Mastercard · Mada · Apple Pay</span>
                </div>
              </div>
            ) : (
              /* ── وضع الويب / InAppBrowser: نموذج ميسر مضمّن ───────────────── */
              <>
                {/* نموذج ميسر — يجب أن يكون الـ div موجودًا دائمًا في DOM */}
                <div
                  className="mysr-form"
                  style={{ direction: 'ltr', minHeight: formReady ? 'auto' : 0 }}
                />

                {!formReady && (
                  <div className="flex items-center justify-center gap-3 py-10 text-slate-500">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary"></div>
                    <span className="text-sm">جاري تحميل بوابة الدفع...</span>
                  </div>
                )}

                {/* بطاقات الاختبار في وضع التطوير */}
                {import.meta.env.DEV && formReady && (
                  <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-xl text-sm text-right">
                    <p className="font-semibold text-blue-800 mb-2">🧪 بطاقات الاختبار</p>
                    <div className="text-blue-700 space-y-1 font-mono text-xs">
                      <p>✅ نجاح: 4111 1111 1111 1111</p>
                      <p>❌ فشل: 4000 0000 0000 0002</p>
                      <p className="font-sans text-blue-600">CVV: أي 3 أرقام | تاريخ انتهاء: أي تاريخ مستقبلي</p>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* أمان + ملاحظة الشروط */}
            <div className="mt-8 space-y-2">
              <div className="flex items-center justify-center gap-2 text-xs text-slate-400">
                <Icon name="Lock" size={12} />
                <span>جميع المعاملات مشفرة وآمنة عبر بوابة ميسر</span>
              </div>
              <p className="text-center text-xs text-slate-400" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>
                بإتمام الدفع، أنت توافق على{' '}
                <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                  الشروط والأحكام
                </a>
                {' '}الخاصة بمنصة مشهد
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingPayment;
